import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { couponService } from '../../../coupons/services/couponService'

interface CouponDisplayInfo {
  id: string
  name: string
  code: string
  discount: number
  type: 'percentage' | 'fixed_amount'
  description?: string
  isValid: boolean
  validityInfo?: string
}

interface DiscountDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  existingDiscounts: CouponDisplayInfo[]
  onAddCoupon: (coupon: CouponDisplayInfo) => void
}

// Helper function to transform coupon data for display
function transformCouponForDisplay(
  coupon: Record<string, unknown>
): CouponDisplayInfo {
  const now = new Date()
  const redeemAfter = coupon.redeem_after
    ? new Date(coupon.redeem_after as string)
    : null
  const redeemBefore = coupon.redeem_before
    ? new Date(coupon.redeem_before as string)
    : null

  let isValid = coupon.status === 'active'
  let validityInfo = ''

  if (redeemAfter && now < redeemAfter) {
    isValid = false
    validityInfo = `Valid from ${redeemAfter.toLocaleDateString()}`
  } else if (redeemBefore && now > redeemBefore) {
    isValid = false
    validityInfo = 'Expired'
  } else if (
    coupon.max_redemptions &&
    coupon.times_redeemed &&
    (coupon.times_redeemed as number) >= (coupon.max_redemptions as number)
  ) {
    isValid = false
    validityInfo = 'Usage limit reached'
  } else if (redeemBefore) {
    validityInfo = `Valid until ${redeemBefore.toLocaleDateString()}`
  }

  // Handle the API response format (snake_case) vs TypeScript interface (camelCase)
  const couponType = (coupon.coupon_type || coupon.type) as string
  const percentageRate = (coupon.percentage_rate ||
    coupon.percentageOff) as number
  const amountCents = (coupon.amount_cents || coupon.amountOff) as number

  return {
    id: coupon.id as string,
    name: coupon.name as string,
    code: (coupon.code || coupon.couponId) as string,
    discount:
      couponType === 'percentage'
        ? percentageRate || 0
        : (amountCents || 0) / 100, // Convert cents to dollars
    type: couponType === 'fixed_amount' ? 'fixed_amount' : 'percentage',
    description: coupon.description as string | undefined,
    isValid,
    validityInfo,
  }
}

export function DiscountDialog({
  open,
  onOpenChange,
  existingDiscounts,
  onAddCoupon,
}: DiscountDialogProps) {
  const [selectedCoupon, setSelectedCoupon] = useState<string>('')
  const [availableCoupons, setAvailableCoupons] = useState<CouponDisplayInfo[]>(
    []
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchCoupons = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await couponService.getCoupons({ page: 1, limit: 100 }) // Fetch all active coupons
      const coupons = response.data
        .filter(coupon => coupon.status === 'active') // Only show active coupons
        .map(transformCouponForDisplay)
      setAvailableCoupons(coupons)
    } catch (err) {
      toast.error(extractErrorMessage(err))
      setError('Failed to load coupons')
    } finally {
      setLoading(false)
    }
  }, [])

  // Fetch coupons when dialog opens
  useEffect(() => {
    if (open) {
      fetchCoupons()
    }
  }, [open, fetchCoupons])

  const handleAddCoupon = () => {
    const coupon = availableCoupons.find(c => c.id === selectedCoupon)
    if (coupon && !existingDiscounts.some(d => d.id === coupon.id)) {
      onAddCoupon(coupon)
      setSelectedCoupon('')
      onOpenChange(false)
    }
  }

  const availableOptions = availableCoupons.filter(
    coupon => !existingDiscounts.some(existing => existing.id === coupon.id)
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg max-h-[80vh]'>
        <DialogHeader>
          <DialogTitle>Add Discount</DialogTitle>
          <p className='text-sm text-muted-foreground'>
            Select a coupon/discount to apply to this contract
          </p>
        </DialogHeader>

        <div className='space-y-4'>
          {error && (
            <div className='p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md'>
              {error}
            </div>
          )}

          <div className='space-y-2'>
            <Label>Select Coupon</Label>
            <Select
              value={selectedCoupon}
              onValueChange={setSelectedCoupon}
              disabled={loading}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    loading ? 'Loading coupons...' : 'Choose a coupon...'
                  }
                >
                  {selectedCoupon &&
                    availableCoupons.find(c => c.id === selectedCoupon)?.name}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {availableOptions.length === 0 && !loading ? (
                  <div className='p-2 text-sm text-muted-foreground text-center'>
                    No coupons available
                  </div>
                ) : (
                  availableOptions.map(coupon => (
                    <SelectItem
                      key={coupon.id}
                      value={coupon.id}
                      disabled={!coupon.isValid}
                    >
                      <div className='flex flex-col w-full'>
                        <div className='flex items-center justify-between'>
                          <span className='font-medium'>{coupon.name}</span>
                          <span className='text-xs text-muted-foreground'>
                            {coupon.code}
                          </span>
                        </div>
                        <div className='flex items-center justify-between'>
                          <span className='text-sm text-muted-foreground'>
                            {coupon.type === 'percentage'
                              ? `${coupon.discount}% off`
                              : `$${coupon.discount} off`}
                            {coupon.description && ` - ${coupon.description}`}
                          </span>
                          {coupon.validityInfo && (
                            <span
                              className={`text-xs ${coupon.isValid ? 'text-green-600' : 'text-red-600'}`}
                            >
                              {coupon.validityInfo}
                            </span>
                          )}
                        </div>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className='flex justify-end gap-3'>
            <Button variant='outline' onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddCoupon}
              disabled={!selectedCoupon || loading}
            >
              Add Coupon
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
